import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { getService } from "@/lib/api";
import { Reveal } from "@/lib/motion";
import { SectionHeading, Overline } from "@/components/ui-kit";
import LeadForm from "@/components/LeadForm";
import { useSeo } from "@/lib/useSeo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: s, isLoading, isError } = useQuery({ queryKey: ["service", slug], queryFn: () => getService(slug) });
  useSeo({ title: s?.title, description: s?.intro });

  if (isLoading) return <div className="pt-44 text-center text-ink/50">Loading…</div>;
  if (isError || !s) return <div className="pt-44 text-center" data-testid="service-not-found"><p className="font-display text-4xl">Service not found</p><Link to="/services" className="text-bronze link-underline mt-4 inline-block">Back to Services</Link></div>;

  return (
    <div data-testid="service-detail-page">
      <section className="relative h-[62vh] min-h-[440px] overflow-hidden">
        <img src={s.hero_image} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/45" />
        <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col justify-end pb-16">
          <Link to="/services" className="text-ivory/80 text-sm uppercase tracking-[0.2em] font-bold flex items-center gap-2 hover:text-gold mb-6"><ArrowLeft size={16} /> All Services</Link>
          <Overline className="!text-gold">{s.category}</Overline>
          <h1 className="font-display text-ivory text-5xl md:text-7xl leading-none mt-3">{s.title}</h1>
          <p className="text-ivory/80 text-lg max-w-2xl mt-5">{s.tagline}</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <SectionHeading overline="Overview" title="Designed around" italic="you." />
          <Reveal><p className="mt-6 text-lg text-ink/75 leading-relaxed">{s.intro}</p></Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {s.features?.map((f, i) => (
              <Reveal key={f} delay={i * 0.06} className="flex gap-3 items-start">
                <CheckCircle2 className="text-bronze shrink-0 mt-0.5" size={20} />
                <span className="text-ink/80">{f}</span>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <Reveal>
            <div className="bg-cream border border-gold/20 rounded-md p-8">
              <Overline>Transparent BOQ</Overline>
              <h3 className="font-display text-3xl mt-2 mb-6">What&apos;s included</h3>
              <ul className="divide-y divide-gold/15">
                {s.boq?.map((b) => (
                  <li key={b.item} className="py-4">
                    <p className="font-medium">{b.item}</p>
                    <p className="text-ink/55 text-sm mt-0.5">{b.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-olive text-beige grain relative py-20 md:py-28">
        <div className="relative z-10 mx-auto max-w-[1000px] px-6 md:px-10">
          <SectionHeading overline="Get Started" title={`Enquire about ${s.title.toLowerCase()}`} dark center />
          <Reveal className="mt-10 bg-ivory text-ink rounded-lg p-8 md:p-10">
            <LeadForm variant="full" defaultService={s.title} source={`Service: ${s.title}`} />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
